import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Download, Printer, Save, Loader2, Copy, Phone } from "lucide-react";
import { toast } from "sonner";
import { Navbar, ProductImage, ProductCard, ease } from "@/components/shared";
import { steps, visibleSteps, categoriaToStep } from "@/data/configurator";
import { site } from "@/config/site";
import { api, euro } from "@/lib/api";
import { publicUrl } from "@/lib/publicUrl";

const flatSelections = (sel) => {
  const out = [];
  Object.values(sel).forEach((v) => {
    if (Array.isArray(v)) out.push(...v);
    else if (v) out.push(v);
  });
  return out;
};

const hasStepSelection = (sel, key) => {
  const v = sel[key];
  if (Array.isArray(v)) return v.length > 0;
  return !!v;
};

const toItem = (o, stepKey) => ({
  id: o.id || "",
  categoria: stepKey, nome: o.nome, prezzo: o.prezzo || 0,
  immagine: o.immagine || "", dettaglio: [o.materiale, o.finitura].filter(Boolean).join(" · "), quantita: 1,
});

const quoteUrl = (qid) => {
  if (!qid || typeof window === "undefined") return "";
  return `${window.location.origin}${publicUrl(`/preventivo/${qid}`)}`;
};

const OptionCard = ({ o, selected, onClick, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick}
    className={`text-left w-full rounded-xl border p-4 md:p-6 transition-all duration-300 hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-accent ${
      selected ? "border-accent ring-2 ring-accent bg-accent/5" : "border-border bg-card hover:shadow-md"
    }`}>
    <div className="flex items-center justify-between gap-2">
      <h4 className="text-lg md:text-xl font-serif text-primary">{o.nome}</h4>
      {selected && <Check className="text-accent shrink-0" size={20} />}
    </div>
    <p className="mt-1.5 md:mt-2 text-xs md:text-sm text-muted-foreground">{o.descrizione}</p>
    <p className="mt-3 md:mt-4 text-lg md:text-xl font-serif text-primary">{o.prezzo ? euro(o.prezzo) : "Incluso"}</p>
  </button>
);

export default function Configurator() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [idx, setIdx] = useState(0);
  const [sel, setSel] = useState({});
  const [cache, setCache] = useState({});
  const [cliente, setCliente] = useState({ cliente_nome: "", cliente_email: "", cliente_telefono: "", note: "" });
  const [saving, setSaving] = useState(false);
  const [quoteId, setQuoteId] = useState(id || null);
  const [submitted, setSubmitted] = useState(false);
  const [jumpTo, setJumpTo] = useState(null);
  const [privacy, setPrivacy] = useState(false);
  const prefillRef = React.useRef(false);

  const activeSteps = useMemo(() => visibleSteps(sel), [sel]);
  const step = activeSteps[idx] || activeSteps[activeSteps.length - 1];

  useEffect(() => {
    if (idx >= activeSteps.length) setIdx(Math.max(0, activeSteps.length - 1));
  }, [activeSteps.length, idx]);

  useEffect(() => {
    steps.filter((s) => s.source === "catalogo").forEach((s) => {
      if (!cache[s.categoria]) {
        api.catalog(s.categoria)
          .then((d) => setCache((c) => ({ ...c, [s.categoria]: d })))
          .catch(() => setCache((c) => ({ ...c, [s.categoria]: [] })));
      }
    });
  }, []); // eslint-disable-line

  useEffect(() => {
    const pid = searchParams.get("prodotto");
    if (!pid || prefillRef.current) return;
    prefillRef.current = true;
    api.product(pid).then((p) => {
      const stepKey = categoriaToStep[p.categoria];
      if (!stepKey) return;
      const st = steps.find((x) => x.key === stepKey);
      const item = toItem(p, stepKey);
      const tipOpts = steps[0].options;
      setSel((s) => {
        const next = { ...s };
        if (st?.tipo === "multi") {
          const arr = s[stepKey] || [];
          const exists = arr.find((x) => x.id && x.id === item.id);
          next[stepKey] = exists ? arr : [...arr, item];
        } else next[stepKey] = item;
        if (p.categoria === "urne") {
          next.tipologia = tipOpts.find((o) => o.nome === "Cremazione") || s.tipologia;
        } else if (p.categoria === "lapidi") {
          const loculo = (p.varianti || []).includes("Loculo");
          next.tipologia = tipOpts.find((o) => o.nome === (loculo ? "Tumulazione" : "Inumazione")) || s.tipologia;
        }
        return next;
      });
      setJumpTo(stepKey);
      toast.success(`${p.nome} aggiunto al preventivo`);
    }).catch(() => toast.error("Prodotto non trovato"));
    setSearchParams((prev) => {
      const n = new URLSearchParams(prev);
      n.delete("prodotto");
      return n;
    }, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!jumpTo) return;
    const vis = visibleSteps(sel);
    const i = vis.findIndex((st) => st.key === jumpTo);
    if (i >= 0) {
      setIdx(i);
      setJumpTo(null);
    }
  }, [jumpTo, sel]);

  useEffect(() => {
    if (!id) return;
    api.getQuote(id).then((q) => {
      setCliente({ cliente_nome: q.cliente_nome || "", cliente_email: q.cliente_email || "", cliente_telefono: q.cliente_telefono || "", note: q.note || "" });
      const s = {};
      if (q.tipologia) {
        const tip = steps[0].options.find((o) => o.nome === q.tipologia);
        if (tip) s.tipologia = tip;
      }
      (q.items || []).forEach((it) => {
        const st = steps.find((x) => x.key === it.categoria);
        if (!st) return;
        if (st.tipo === "multi") s[st.key] = [...(s[st.key] || []), it];
        else s[st.key] = it;
      });
      setSel(s);
    }).catch(() => toast.error("Preventivo non trovato"));
  }, [id]);

  const items = useMemo(() => flatSelections(sel), [sel]);
  const subtotale = items.reduce((a, b) => a + (b.prezzo || 0), 0);
  const iva = subtotale * 0.22;
  const totale = subtotale + iva;

  const sameOpt = (a, b) => {
    if (!a || !b) return false;
    if (a.id && b.id) return a.id === b.id;
    return a.nome === b.nome && Number(a.prezzo || 0) === Number(b.prezzo || 0);
  };

  const pick = (o) => {
    const item = toItem(o, step.key);
    setSel((s) => {
      if (step.tipo === "multi") {
        const arr = s[step.key] || [];
        const exists = arr.find((x) => sameOpt(x, item));
        return { ...s, [step.key]: exists ? arr.filter((x) => !sameOpt(x, item)) : [...arr, item] };
      }
      const cur = s[step.key];
      const next = { ...s, [step.key]: sameOpt(cur, item) ? null : item };
      if (step.key === "tipologia") {
        const tip = next.tipologia?.nome;
        if (tip !== "Cremazione") next.urna = null;
        if (tip === "Cremazione") next.lapide = null;
        else if (tip !== s.tipologia?.nome) next.lapide = null;
      }
      return next;
    });
  };

  const isSel = (o) => {
    const v = sel[step.key];
    const item = toItem(o, step.key);
    if (Array.isArray(v)) return !!v.find((x) => sameOpt(x, item));
    return sameOpt(v, item);
  };

  // Evita doppioni (stessa foto o stesso nome) se il DB non è ancora aggiornato
  const dedupeOptions = (list) => {
    const seenImg = new Set();
    const seenNome = new Set();
    return list.filter((p) => {
      const img = p.immagine || "";
      const nome = p.nome || "";
      if (img && seenImg.has(img)) return false;
      if (nome && seenNome.has(nome)) return false;
      if (img) seenImg.add(img);
      if (nome) seenNome.add(nome);
      return true;
    });
  };

  let options = step?.source === "catalogo" ? (cache[step.categoria] || []) : step?.options || [];
  const catalogLoading = step?.source === "catalogo" && cache[step.categoria] === undefined;
  if (step?.source === "catalogo") options = dedupeOptions(options);
  if (step?.filterVariante) {
    const want = step.filterVariante(sel);
    options = options.filter((p) => (p.varianti || []).includes(want));
  }

  const selected = hasStepSelection(sel, step?.key);
  const canNext = step?.tipo === "summary" || step?.optional || selected;
  const sottotitolo = step?.sottotitoloFor?.[sel.tipologia?.nome] || step?.sottotitolo;

  const persist = async () => {
    const payload = { ...cliente, tipologia: sel.tipologia?.nome || "", items };
    if (quoteId) { await api.updateQuote(quoteId, payload); return quoteId; }
    const q = await api.createQuote(payload);
    setQuoteId(q.id);
    navigate(`/preventivo/${q.id}`, { replace: true });
    return q.id;
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      const qid = await persist();
      const url = quoteUrl(qid);
      toast.success(url ? `Preventivo salvato. Conservi questo link: ${url}` : "Preventivo salvato. Può riprenderlo in seguito.");
      return qid;
    }
    catch { toast.error("Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  const richiedi = async () => {
    if (!cliente.cliente_nome) return toast.error("Inserisca il nome");
    if (!privacy) return toast.error("Accetti l'informativa privacy per inviare la richiesta.");
    setSaving(true);
    try {
      const qid = await persist();
      toast.success("Richiesta inviata. La contatteremo a breve.");
      if (cliente.cliente_email) { await api.sendPdf(qid).catch(() => {}); }
      setSubmitted(true);
    } catch { toast.error("Errore nell'invio"); }
    finally { setSaving(false); }
  };

  const copyLink = async () => {
    if (!quoteId) return;
    try {
      await navigator.clipboard.writeText(quoteUrl(quoteId));
      toast.success("Link copiato. Lo conservi per riprendere il preventivo.");
    } catch { toast.error("Non è stato possibile copiare il link"); }
  };

  const bloccaEStampa = async () => {
    setSaving(true);
    try {
      const qid = await persist();
      toast.success("Preventivo bloccato. Apertura PDF...");
      window.open(api.pdfUrl(qid), "_blank");
    } catch { toast.error("Errore nella generazione del PDF"); }
    finally { setSaving(false); }
  };

  const progress = ((idx + 1) / activeSteps.length) * 100;

  if (submitted) {
    return (
      <div data-testid="configurator-page" className="min-h-screen bg-secondary">
        <Navbar />
        <div className="max-w-xl mx-auto px-6 pt-header pb-16 text-center" data-testid="quote-confirmation">
          <p className="overline mb-4">Richiesta ricevuta</p>
          <h1 className="text-3xl md:text-4xl font-serif font-light text-primary">Grazie{cliente.cliente_nome ? `, ${cliente.cliente_nome}` : ""}</h1>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            Abbiamo ricevuto il preventivo{quoteId ? ` (rif. #${quoteId.slice(0, 8)})` : ""}. Un consulente la ricontatterà a breve. Siamo disponibili 24 ore su 24.
          </p>
          <p className="mt-2 font-serif text-2xl text-primary">{euro(totale)} <span className="text-base text-muted-foreground">IVA inclusa</span></p>
          {quoteId && (
            <div className="mt-8 bg-card border border-border rounded-xl p-5 text-left">
              <p className="text-sm text-muted-foreground">Conservi questo link per rivedere o modificare il preventivo:</p>
              <p className="mt-2 text-sm text-primary break-all">{quoteUrl(quoteId)}</p>
              <button type="button" onClick={copyLink} className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-primary">
                <Copy size={15} /> Copia il link
              </button>
            </div>
          )}
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a href={`tel:${site.telefonoRaw}`} className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-semibold px-6 py-3 rounded-full">
              <Phone size={16} /> {site.telefono}
            </a>
            <Link to="/" className="inline-flex items-center gap-2 border border-border px-6 py-3 rounded-full text-primary">Torna al sito</Link>
            {quoteId && (
              <a href={api.pdfUrl(quoteId)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-border px-6 py-3 rounded-full text-primary">
                <Download size={16} /> PDF
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="configurator-page" className="min-h-screen bg-secondary">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 pt-header pb-28 md:pb-16">
        <div className="mb-5 md:mb-8">
          <Link to="/" className="text-sm text-muted-foreground hover:text-primary">← Torna al sito</Link>
          <h1 className="mt-2 md:mt-3 text-2xl md:text-4xl font-serif font-light text-primary">Crea il tuo preventivo</h1>
          <div className="mt-3 md:mt-4 flex items-center justify-between">
            <p className="overline">Passo {idx + 1} di {activeSteps.length}</p>
            <p className="text-sm text-muted-foreground">{Math.round(progress)}%</p>
          </div>
          <div className="mt-2 md:mt-3 h-1.5 bg-border rounded-full overflow-hidden">
            <motion.div className="h-full bg-accent" animate={{ width: `${progress}%` }} transition={{ ease }} />
          </div>
        </div>

        <div className="grid lg:grid-cols-[1fr_360px] gap-6 md:gap-12">
          <div>
            <AnimatePresence mode="wait">
              <motion.div key={step.key}
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.4, ease }}>
                <h2 className="text-2xl md:text-4xl font-serif font-light text-primary">{step.titolo}</h2>
                <p className="mt-1.5 md:mt-2 text-muted-foreground text-sm md:text-lg">{sottotitolo}</p>

                {step.tipo === "summary" ? (
                  <Summary items={items} subtotale={subtotale} iva={iva} totale={totale}
                    cliente={cliente} setCliente={setCliente} onEdit={() => setIdx(0)}
                    onRequest={richiedi} onLock={bloccaEStampa} saving={saving} quoteId={quoteId}
                    privacy={privacy} setPrivacy={setPrivacy} />
                ) : catalogLoading ? (
                  <div className="mt-5 md:mt-8 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-5">
                    {[...Array(6)].map((_, i) => (
                      <div key={i} className="rounded-xl border border-border bg-card overflow-hidden animate-pulse">
                        <div className="aspect-[4/3] bg-secondary" />
                        <div className="p-5 space-y-2"><div className="h-4 bg-secondary rounded w-2/3" /><div className="h-3 bg-secondary rounded w-full" /></div>
                      </div>
                    ))}
                  </div>
                ) : step.source === "catalogo" ? (
                  <div className="mt-5 md:mt-8 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-5">
                    {options.length === 0 && (
                      <p className="col-span-full text-muted-foreground">
                        Nessun prodotto disponibile. Ci chiami al{" "}
                        <a href={`tel:${site.telefonoRaw}`} className="text-primary font-semibold">{site.telefono}</a>.
                      </p>
                    )}
                    {options.map((p) => (
                      <ProductCard key={p.id} p={p} selected={isSel(p)} onClick={() => pick(p)} testid={`opt-${p.id}`} />
                    ))}
                  </div>
                ) : (
                  <div className="mt-5 md:mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-5">
                    {options.map((o, i) => (
                      <OptionCard key={i} o={o} selected={isSel(o)} onClick={() => pick(o)} testid={`opt-${step.key}-${i}`} />
                    ))}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          <aside className="lg:sticky lg:top-36 h-fit">
            <div className="bg-card border border-border rounded-2xl p-6 hidden lg:block">
              <p className="overline mb-4">Riepilogo</p>
              <div className="space-y-3 max-h-64 overflow-auto pr-1">
                {items.length === 0 && <p className="text-sm text-muted-foreground">Nessuna selezione</p>}
                {items.map((it, i) => (
                  <div key={i} className="flex items-center gap-3">
                    {it.immagine && <ProductImage src={it.immagine} alt="" className="w-11 h-11 rounded-md object-cover" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-primary truncate">{it.nome}</p>
                    </div>
                    <span className="text-sm font-medium text-primary whitespace-nowrap">{euro(it.prezzo)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-5 pt-4 border-t border-border space-y-1 text-sm">
                <div className="flex justify-between text-muted-foreground"><span>Subtotale</span><span>{euro(subtotale)}</span></div>
                <div className="flex justify-between text-muted-foreground"><span>IVA 22%</span><span>{euro(iva)}</span></div>
                <div className="flex justify-between text-primary font-serif text-2xl pt-2"><span>Totale</span><span data-testid="running-total">{euro(totale)}</span></div>
              </div>
              <button onClick={saveDraft} disabled={saving} data-testid="save-draft"
                className="mt-4 w-full flex items-center justify-center gap-2 border border-border text-primary py-2.5 rounded-full text-sm hover:bg-secondary transition-colors">
                <Save size={15} /> Salva preventivo
              </button>
              {quoteId && (
                <button type="button" onClick={copyLink} className="mt-2 w-full flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-primary">
                  <Copy size={14} /> Copia link di ripresa
                </button>
              )}
            </div>
          </aside>
        </div>
      </div>

      <div className="fixed bottom-0 inset-x-0 z-40 bg-white/90 backdrop-blur-xl border-t border-border">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 md:px-12 py-2.5 md:py-4 flex items-center justify-between gap-2 sm:gap-4 min-w-0">
          <button onClick={() => setIdx(Math.max(0, idx - 1))} disabled={idx === 0} data-testid="btn-indietro"
            className="inline-flex items-center gap-1.5 shrink-0 px-3 sm:px-6 py-2.5 sm:py-3 rounded-full border border-border text-primary text-sm sm:text-base disabled:opacity-40 hover:bg-secondary transition-colors">
            <ArrowLeft size={16} className="sm:w-[18px] sm:h-[18px]" />
            <span>Indietro</span>
          </button>
          <div className="lg:hidden text-center min-w-0 flex-1 px-1">
            <p className="text-[10px] sm:text-xs text-muted-foreground">Totale</p>
            <p className="font-serif text-base sm:text-xl text-primary truncate" data-testid="running-total-mobile">{euro(totale)}</p>
          </div>
          {idx < activeSteps.length - 1 ? (
            <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
              {step.optional && !selected && (
                <button onClick={() => setIdx(idx + 1)} data-testid="btn-salta"
                  className="text-xs sm:text-sm text-muted-foreground hover:text-primary underline underline-offset-4 px-1">
                  Salta
                </button>
              )}
              <button onClick={() => canNext && setIdx(idx + 1)} disabled={!canNext} data-testid="btn-avanti"
                className="inline-flex items-center gap-1.5 px-3.5 sm:px-8 py-2.5 sm:py-3 rounded-full bg-primary text-primary-foreground text-sm sm:text-base disabled:opacity-40 hover:bg-primary/90 transition-colors">
                Avanti
                <ArrowRight size={16} className="sm:w-[18px] sm:h-[18px]" />
              </button>
            </div>
          ) : (
            <button onClick={richiedi} disabled={saving} data-testid="btn-richiedi-bottom"
              className="inline-flex items-center gap-1.5 shrink-0 px-3 sm:px-8 py-2.5 sm:py-3 rounded-full bg-accent text-primary font-semibold text-sm sm:text-base hover:bg-accent/90 transition-colors">
              {saving ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />}
              <span className="sm:hidden">Richiedi</span>
              <span className="hidden sm:inline">Richiedi preventivo</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const fieldClass = "mt-1.5 w-full border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-accent";

const Summary = ({ items, subtotale, iva, totale, cliente, setCliente, onEdit, onRequest, onLock, saving, quoteId, privacy, setPrivacy }) => (
  <div className="mt-8" data-testid="summary">
    <div className="bg-card border border-border rounded-2xl overflow-hidden">
      {items.length === 0 && <p className="p-8 text-muted-foreground">Nessuna selezione effettuata.</p>}
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-4 p-4 border-b border-border last:border-0">
          {it.immagine
            ? <ProductImage src={it.immagine} alt="" className="w-16 h-16 rounded-lg object-cover" />
            : <div className="w-16 h-16 rounded-lg bg-accent/15 flex items-center justify-center"><Check className="text-accent" /></div>}
          <div className="flex-1 min-w-0">
            <p className="font-serif text-lg text-primary">{it.nome}</p>
            {it.dettaglio && <p className="text-xs text-muted-foreground">{it.dettaglio}</p>}
          </div>
          <span className="font-medium text-primary">{euro(it.prezzo)}</span>
        </div>
      ))}
      <div className="p-6 bg-secondary space-y-1">
        <div className="flex justify-between text-muted-foreground"><span>Subtotale</span><span>{euro(subtotale)}</span></div>
        <div className="flex justify-between text-muted-foreground"><span>IVA 22%</span><span>{euro(iva)}</span></div>
        <div className="flex justify-between text-primary font-serif text-3xl pt-2"><span>Totale</span><span data-testid="summary-total">{euro(totale)}</span></div>
      </div>
    </div>

    <div className="mt-8 grid sm:grid-cols-2 gap-4">
      <label className="text-sm font-medium text-primary">Nome e cognome
        <input placeholder="Nome e cognome" required data-testid="sum-nome" value={cliente.cliente_nome}
          onChange={(e) => setCliente({ ...cliente, cliente_nome: e.target.value })}
          className={fieldClass} />
      </label>
      <label className="text-sm font-medium text-primary">Email
        <input placeholder="Email" type="email" data-testid="sum-email" value={cliente.cliente_email}
          onChange={(e) => setCliente({ ...cliente, cliente_email: e.target.value })}
          className={fieldClass} />
      </label>
      <label className="text-sm font-medium text-primary">Telefono
        <input placeholder="Telefono" data-testid="sum-tel" value={cliente.cliente_telefono}
          onChange={(e) => setCliente({ ...cliente, cliente_telefono: e.target.value })}
          className={fieldClass} />
      </label>
      <label className="text-sm font-medium text-primary">Note
        <input placeholder="Note" data-testid="sum-note" value={cliente.note}
          onChange={(e) => setCliente({ ...cliente, note: e.target.value })}
          className={fieldClass} />
      </label>
    </div>
    <label className="mt-4 flex items-start gap-3 text-sm text-muted-foreground">
      <input type="checkbox" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} className="mt-1" data-testid="sum-privacy" />
      <span>Ho letto e accetto l’<Link to="/privacy" className="text-primary underline underline-offset-2">informativa privacy</Link>.</span>
    </label>

    <div className="mt-6 flex flex-wrap gap-3">
      <button onClick={onEdit} data-testid="sum-modifica" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-border text-primary hover:bg-secondary transition-colors">Modifica</button>
      <button onClick={onRequest} disabled={saving} data-testid="sum-richiedi" className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-accent text-primary font-semibold hover:bg-accent/90 transition-colors">
        {saving ? <Loader2 className="animate-spin" size={18} /> : <Check size={18} />} Richiedi preventivo
      </button>
      <button onClick={onLock} disabled={saving} data-testid="sum-blocca" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors">
        <Download size={18} /> Scarica PDF
      </button>
      {quoteId && (
        <a href={api.pdfUrl(quoteId)} target="_blank" rel="noreferrer" data-testid="sum-pdf" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-border text-primary hover:bg-secondary transition-colors"><Printer size={18} /> Stampa</a>
      )}
    </div>
    <p className="mt-4 text-xs text-muted-foreground">Preventivo gratuito e non vincolante · {site.orari}</p>
  </div>
);

